// useOrganizationDocumentProcessing.ts - Organization-level document processing websocket management
import { useUserStore } from '@/shared/stores/userStore'
import { WebSocketMessageType } from '@/utils/channels/types'
import { computed, onUnmounted, readonly, ref, watch } from 'vue'
import { useChannelSubscription } from './useChannelSubscription'

export interface OrganizationDocumentMessage {
  document_uuid: string
  status: 'STARTED' | 'FINISHED' | 'FAILED'
  error?: string
}

export interface OrganizationDocumentProcessingState {
  isConnected: boolean
  isConnecting: boolean
  hasError: boolean
  lastMessage: any
  error: any
}

/**
 * Composable for managing organization-level document processing websocket.
 *
 * This replaces individual document websockets with a single organization-level
 * websocket that receives all document processing updates for the user's organization.
 *
 * @param onDocumentUpdate - Callback function called when a document update is received
 * @returns Organization document processing state and control methods
 */
export function useOrganizationDocumentProcessing(
  onDocumentUpdate?: (message: OrganizationDocumentMessage) => void
) {
  const userStore = useUserStore()

  // Get organization UUID from user store
  const organizationUuid = computed(() => userStore.user?.org?.uuid)

  // Build channel name for organization documents
  const channelName = computed(() =>
    organizationUuid.value ? `org_documents_${organizationUuid.value}` : ''
  )

  // Create websocket subscription with manual lifecycle management
  const {
    connectionStatus,
    lastMessage,
    lastError,
    isConnected,
    isConnecting,
    hasError,
    subscribe,
    unsubscribe,
    reconnect
  } = useChannelSubscription(channelName, { disableAutoLifecycle: true })

  // Track subscription state
  const isSubscribed = ref(false)

  // Watch for messages and filter for document updates
  watch(lastMessage, (message) => {
    if (!message) return

    // Handle document state updates
    if (message.type === WebSocketMessageType.DOCUMENT_STATE_UPDATE) {
      const documentMessage: OrganizationDocumentMessage = {
        document_uuid: message.data.document_uuid,
        status: message.data.status,
        error: message.data.error
      }

      // Call the callback if provided
      if (onDocumentUpdate) {
        onDocumentUpdate(documentMessage)
      }
    }
  })

  // Watch for organization changes and manage subscription
  watch(
    organizationUuid,
    (newOrgUuid, oldOrgUuid) => {
      // If organization changed, unsubscribe from old and subscribe to new
      if (oldOrgUuid && newOrgUuid !== oldOrgUuid) {
        unsubscribe()
        isSubscribed.value = false
      }

      // If we have a valid organization, subscribe
      if (newOrgUuid && !isSubscribed.value) {
        subscribe()
        isSubscribed.value = true
      }
    },
    { immediate: true }
  )

  // Cleanup on component unmount
  onUnmounted(() => {
    stopSubscription()
  })

  // Manual subscription control
  const startSubscription = () => {
    if (organizationUuid.value && !isSubscribed.value) {
      subscribe()
      isSubscribed.value = true
    }
  }

  const stopSubscription = () => {
    if (isSubscribed.value) {
      unsubscribe()
      isSubscribed.value = false
    }
  }

  // Return state and control methods
  return {
    // State
    isConnected: readonly(isConnected),
    isConnecting: readonly(isConnecting),
    hasError: readonly(hasError),
    lastMessage: readonly(lastMessage),
    error: readonly(lastError),
    isSubscribed: readonly(isSubscribed),
    organizationUuid: readonly(organizationUuid),

    // Control methods
    startSubscription,
    stopSubscription,
    reconnect,

    // Raw connection status for debugging
    connectionStatus: readonly(connectionStatus)
  }
}

/**
 * Helper function to create a document update handler that can be used
 * with the existing useDocumentProcessing composable.
 *
 * @param updateDocumentStatusByMessage - Function from useDocumentProcessing
 * @returns Handler function for organization document messages
 */
export function createOrganizationDocumentHandler(
  updateDocumentStatusByMessage: (documentUuid: string, status: string, error?: string) => void
) {
  return (message: OrganizationDocumentMessage) => {
    updateDocumentStatusByMessage(message.document_uuid, message.status, message.error)
  }
}
